import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  Download,
  Users,
  AlertTriangle,
  PenLine,
  BarChart3,
  DollarSign,
  Receipt,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import api from '../../lib/api';
import { money } from '../../lib/money';
import { formatAccountName } from '../../utils/accounts';
import {
  Card,
  CardHeader,
  CardTitle,
  DateText,
  EmptyState,
  MetricCard,
  Money,
  Button,
  Skeleton,
  SkeletonTable,
  useToast,
} from '../ui';

interface ReportsTabProps {
  caseId: string;
  clientName?: string | null;
  opposingPartyName?: string | null;
}

interface SpouseData {
  total_transactions: number;
  totals: Record<string, number>;
  waste_count: number;
}

interface ReportSummaryData {
  property_totals: Record<string, number>;
  client: SpouseData;
  opposing: SpouseData;
  waste_claims_count: number;
  waste_claims_total: number;
  overrides_count: number;
  total_transactions: number;
}

interface CostStatementItem {
  id: number;
  source_file: string;
  bank_label: string | null;
  created_at: string | null;
  total_cost_usd: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_by_model: Record<string, { input_tokens: number; output_tokens: number; cost_usd: number }> | null;
}

interface CostSummaryData {
  case_id: number;
  total_cost_usd: number;
  statement_count: number;
  statements: CostStatementItem[];
}

/** Plain-count formatting (transactions, tokens) — money goes through money(). */
const NUM = new Intl.NumberFormat('en-US');

function ComparisonBar({
  label,
  husbandValue,
  wifeValue,
  husbandLabel = 'Client',
  wifeLabel = 'Opposing party',
}: {
  label: string;
  husbandValue: number;
  wifeValue: number;
  husbandLabel?: string;
  wifeLabel?: string;
}) {
  const max = Math.max(Math.abs(husbandValue), Math.abs(wifeValue), 1);
  const hPct = (Math.abs(husbandValue) / max) * 100;
  const wPct = (Math.abs(wifeValue) / max) * 100;

  return (
    <div className="mb-4">
      <p className="text-sm font-medium text-ink-soft mb-2">{label}</p>
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-faint w-24 text-right truncate" title={husbandLabel}>{husbandLabel}</span>
          <div className="flex-1 bg-cream-dark rounded-full h-5 overflow-hidden">
            <div
              className="h-full bg-green-700 rounded-full flex items-center justify-end pr-2"
              style={{ width: `${Math.max(hPct, 2)}%` }}
            >
              {hPct > 20 && (
                <span className="text-xs text-white font-medium">
                  {money(husbandValue, { whole: true })}
                </span>
              )}
            </div>
          </div>
          {hPct <= 20 && (
            <span className="text-xs text-ink-soft">{money(husbandValue, { whole: true })}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-faint w-24 text-right truncate" title={wifeLabel}>{wifeLabel}</span>
          <div className="flex-1 bg-cream-dark rounded-full h-5 overflow-hidden">
            <div
              className="h-full bg-good rounded-full flex items-center justify-end pr-2"
              style={{ width: `${Math.max(wPct, 2)}%` }}
            >
              {wPct > 20 && (
                <span className="text-xs text-white font-medium">
                  {money(wifeValue, { whole: true })}
                </span>
              )}
            </div>
          </div>
          {wPct <= 20 && (
            <span className="text-xs text-ink-soft">{money(wifeValue, { whole: true })}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReportsTab({ caseId, clientName, opposingPartyName }: ReportsTabProps) {
  const [downloading, setDownloading] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [costReceiptOpen, setCostReceiptOpen] = useState(false);
  const { toast } = useToast();

  const { data: summary, isLoading } = useQuery<ReportSummaryData>({
    queryKey: ['report-summary', caseId],
    queryFn: async () => {
      const res = await api.get(`/cases/${caseId}/report/summary`);
      return res.data;
    },
  });

  const { data: costSummary } = useQuery<CostSummaryData>({
    queryKey: ['cost-summary', caseId],
    queryFn: async () => {
      const res = await api.get(`/cases/${caseId}/cost-summary`);
      return res.data;
    },
    enabled: costReceiptOpen,
  });

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/cases/${caseId}/report/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const match = disposition?.match(/filename="(.+)"/);
      link.download = match ? match[1] : 'report.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Failed to generate PDF report.', tone: 'danger' });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadDocx = async () => {
    setDownloadingDocx(true);
    try {
      const res = await api.get(`/cases/${caseId}/report/docx`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const match = disposition?.match(/filename="(.+)"/);
      link.download = match ? match[1] : 'report.docx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Failed to generate Word report.', tone: 'danger' });
    } finally {
      setDownloadingDocx(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton variant="rect" width={180} height={24} />
            <Skeleton variant="rect" width={320} height={16} />
          </div>
          <Skeleton variant="rect" width={360} height={32} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rect" height={96} className="w-full" />
          ))}
        </div>
        <SkeletonTable rows={5} columns={2} />
      </div>
    );
  }

  if (!summary) {
    return (
      <EmptyState
        icon={FileText}
        title="No report data available"
        description="Upload statements first."
      />
    );
  }

  const hTotals = summary.client.totals || {};
  const wTotals = summary.opposing.totals || {};

  const hCommunityCredits = hTotals['community_credits'] || 0;
  const hCommunityDebits = Math.abs(hTotals['community_debits'] || 0);
  const wCommunityCredits = wTotals['community_credits'] || 0;
  const wCommunityDebits = Math.abs(wTotals['community_debits'] || 0);

  const hCommunityNet = hCommunityCredits - hCommunityDebits;
  const wCommunityNet = wCommunityCredits - wCommunityDebits;

  const hSeparateCredits = hTotals['separate_credits'] || 0;
  const hSeparateDebits = Math.abs(hTotals['separate_debits'] || 0);
  const wSeparateCredits = wTotals['separate_credits'] || 0;
  const wSeparateDebits = Math.abs(wTotals['separate_debits'] || 0);

  const propTotals = summary.property_totals || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="t-section text-green-900">Case Report</h2>
          <p className="text-sm text-ink-faint mt-1">
            Summary of property classification, waste claims, and overrides
          </p>
        </div>
        <div className="flex gap-2">
          <Button icon={Download} onClick={handleDownloadDocx} loading={downloadingDocx} variant="secondary">
            Generate Word Report
          </Button>
          <Button icon={Download} onClick={handleDownloadPdf} loading={downloading}>
            Generate PDF Report
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Transactions"
          value={NUM.format(summary.total_transactions)}
          icon={<BarChart3 size={20} />}
        />
        <MetricCard
          label="Potential Waste Claims (for review)"
          value={NUM.format(summary.waste_claims_count)}
          icon={<AlertTriangle size={20} />}
          subtitle={money(summary.waste_claims_total, { whole: true })}
        />
        <MetricCard
          label="Manual Overrides"
          value={NUM.format(summary.overrides_count)}
          icon={<PenLine size={20} />}
        />
        <MetricCard
          label="Parties Analyzed"
          value={
            (summary.client.total_transactions > 0 ? 1 : 0) +
            (summary.opposing.total_transactions > 0 ? 1 : 0)
          }
          icon={<Users size={20} />}
          subtitle={`${clientName || 'H'}: ${summary.client.total_transactions} / ${opposingPartyName || 'W'}: ${summary.opposing.total_transactions}`}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <div className="flex items-center gap-2">
              <Users size={16} />
              Spouse Comparison
            </div>
          </CardTitle>
        </CardHeader>
        <ComparisonBar
          label="Community Income (Credits)"
          husbandValue={hCommunityCredits}
          wifeValue={wCommunityCredits}
          husbandLabel={clientName || 'Client'}
          wifeLabel={opposingPartyName || 'Opposing party'}
        />
        <ComparisonBar
          label="Community Expenses (Debits)"
          husbandValue={hCommunityDebits}
          wifeValue={wCommunityDebits}
          husbandLabel={clientName || 'Client'}
          wifeLabel={opposingPartyName || 'Opposing party'}
        />
        <ComparisonBar
          label="Community Net"
          husbandValue={hCommunityNet}
          wifeValue={wCommunityNet}
          husbandLabel={clientName || 'Client'}
          wifeLabel={opposingPartyName || 'Opposing party'}
        />
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <div className="flex items-center gap-2">
              <FileText size={16} />
              Property Status Summary
            </div>
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          {/* eslint-disable-next-line no-restricted-syntax -- two-column in-card summary; restyled in place, DataTable chrome would double-border it */}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-4 t-eyebrow">Category</th>
                <th className="text-right py-2 px-4 t-eyebrow">Amount</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(propTotals).map(([key, value]) => (
                <tr key={key} className="border-b border-border-subtle last:border-0">
                  <td className="py-2 pr-4 text-ink capitalize">
                    {key.replace(/_/g, ' ')}
                  </td>
                  <td className="py-2 px-4 text-right">
                    <Money value={value as number} whole />
                  </td>
                </tr>
              ))}
              {Object.keys(propTotals).length === 0 && (
                <tr>
                  <td colSpan={2} className="py-4 text-center text-ink-faint">
                    No property data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} />
                Potential Waste — Summary (for attorney review)
              </div>
            </CardTitle>
          </CardHeader>
          {summary.waste_claims_count > 0 ? (
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-ink-soft">Total Flagged (for review)</span>
                <span className="font-semibold text-green-900">
                  {summary.waste_claims_count}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-ink-soft">Total Amount</span>
                <span className="font-semibold text-danger">
                  {money(summary.waste_claims_total, { whole: true })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-ink-soft">{clientName || 'Client'} — flagged</span>
                <span className="font-semibold text-green-900">
                  {summary.client.waste_count}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-ink-soft">{opposingPartyName || 'Opposing party'} — flagged</span>
                <span className="font-semibold text-green-900">
                  {summary.opposing.waste_count}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-ink-faint text-sm">No waste claims identified</p>
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <div className="flex items-center gap-2">
                <PenLine size={16} />
                Override Summary
              </div>
            </CardTitle>
          </CardHeader>
          {summary.overrides_count > 0 ? (
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-ink-soft">Manual Overrides</span>
                <span className="font-semibold text-green-900">
                  {summary.overrides_count}
                </span>
              </div>
              <p className="text-xs text-ink-faint">
                Transactions manually reclassified by the analyst
              </p>
            </div>
          ) : (
            <p className="text-ink-faint text-sm">No manual overrides applied</p>
          )}
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <div className="flex items-center gap-2">
              <DollarSign size={16} />
              Separate Property Summary
            </div>
          </CardTitle>
        </CardHeader>
        <ComparisonBar
          label="Separate Income (Credits)"
          husbandValue={hSeparateCredits}
          wifeValue={wSeparateCredits}
          husbandLabel={clientName || 'Client'}
          wifeLabel={opposingPartyName || 'Opposing party'}
        />
        <ComparisonBar
          label="Separate Expenses (Debits)"
          husbandValue={hSeparateDebits}
          wifeValue={wSeparateDebits}
          husbandLabel={clientName || 'Client'}
          wifeLabel={opposingPartyName || 'Opposing party'}
        />
      </Card>

      <Card padding="none" className="overflow-hidden">
        {/* eslint-disable-next-line no-restricted-syntax -- full-width disclosure row: no ui primitive fits this layout */}
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-cream-50 transition-colors text-left"
          onClick={() => setCostReceiptOpen(o => !o)}
        >
          <div className="flex items-center gap-2 text-sm font-medium text-ink-soft">
            <Receipt size={16} />
            Model API Receipt
          </div>
          {costReceiptOpen ? (
            <ChevronDown size={16} className="text-ink-faint" />
          ) : (
            <ChevronRight size={16} className="text-ink-faint" />
          )}
        </button>

        {costReceiptOpen && (
          <div className="px-4 pb-4 pt-2 border-t border-border-subtle">
            <p className="text-xs text-ink-faint mb-3">
              Processing cost estimates based on standard provider list prices — verify against your actual provider dashboard.
            </p>
            {!costSummary ? (
              <SkeletonTable rows={3} columns={6} />
            ) : costSummary.statements.filter(s => s.total_cost_usd !== null).length === 0 ? (
              <p className="text-sm text-ink-faint text-center py-4">No cost data recorded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                {/* eslint-disable-next-line no-restricted-syntax -- receipt table with tfoot total; restyled in place, DataTable has no footer row */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-3 t-eyebrow text-left">File</th>
                      <th className="py-2 pr-3 t-eyebrow text-left">Account</th>
                      <th className="py-2 pr-3 t-eyebrow text-left">Date Processed</th>
                      <th className="py-2 pr-3 t-eyebrow text-right">Tokens In</th>
                      <th className="py-2 pr-3 t-eyebrow text-right">Tokens Out</th>
                      <th className="py-2 t-eyebrow text-right">Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costSummary.statements
                      .filter(s => s.total_cost_usd !== null)
                      .map(s => (
                        <tr key={s.id} className="border-b border-border-subtle last:border-0">
                          <td className="py-2 pr-3 text-ink max-w-40 truncate" title={s.source_file}>
                            {s.source_file}
                          </td>
                          <td className="py-2 pr-3 text-ink-soft max-w-28 truncate" title={s.bank_label ? formatAccountName(s.bank_label) : ''}>
                            {s.bank_label ? formatAccountName(s.bank_label) : '—'}
                          </td>
                          <td className="py-2 pr-3 text-ink-soft whitespace-nowrap">
                            <DateText value={s.created_at} />
                          </td>
                          <td className="py-2 pr-3 text-right t-num text-ink-soft">
                            {s.input_tokens != null ? NUM.format(s.input_tokens) : '—'}
                          </td>
                          <td className="py-2 pr-3 text-right t-num text-ink-soft">
                            {s.output_tokens != null ? NUM.format(s.output_tokens) : '—'}
                          </td>
                          {/* API costs are sub-cent; money()'s pinned 2 decimals would erase them. */}
                          <td className="py-2 text-right t-num text-green-700">
                            ${s.total_cost_usd!.toFixed(4)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-green-200">
                      <td colSpan={5} className="py-2 pr-3 text-sm font-semibold text-ink-soft">
                        Total ({costSummary.statements.filter(s => s.total_cost_usd !== null).length} statements)
                      </td>
                      <td className="py-2 text-right t-num font-semibold text-green-800">
                        ${costSummary.total_cost_usd.toFixed(4)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
